import type { AdminMailCampaignKind } from '../../services/adminApi'

interface CampaignKindPickerProps {
  value: AdminMailCampaignKind
  onChange: (kind: AdminMailCampaignKind) => void
  locked?: boolean
}

const OPTIONS: {
  kind: AdminMailCampaignKind
  title: string
  description: string
  icon: string
}[] = [
  {
    kind: 'marketing',
    title: 'Announcement',
    description: 'News, features, promotions. Skips anyone who turned off marketing email.',
    icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
  },
  {
    kind: 'service_notice',
    title: 'Service notice',
    description: 'Terms, privacy or account changes everyone must hear about. Reaches every account.',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
]

export function CampaignKindPicker({ value, onChange, locked = false }: CampaignKindPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        What kind of email is this?
      </label>
      <div role="radiogroup" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {OPTIONS.map((opt) => {
          const selected = value === opt.kind
          const disabled = locked && !selected
          return (
            <button
              key={opt.kind}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled || locked}
              onClick={() => onChange(opt.kind)}
              className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200 ${
                selected
                  ? opt.kind === 'service_notice'
                    ? 'border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                    : 'border-[#503864] bg-white dark:border-[#9b6cb8] dark:bg-[#1a1520]'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-[#3d2c4f] dark:bg-[#1a1520] dark:hover:border-[#5a4470]'
              } ${disabled ? 'opacity-40' : ''} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                  selected && opt.kind === 'service_notice'
                    ? 'bg-amber-500 text-white'
                    : selected
                      ? 'bg-[#503864] text-white'
                      : 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300'
                }`}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={opt.icon} />
                </svg>
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold text-gray-900 dark:text-gray-100">{opt.title}</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">{opt.description}</span>
              </span>
            </button>
          )
        })}
      </div>
      {value === 'service_notice' && (
        <p className="text-sm text-amber-800 dark:text-amber-300">
          Only use this for changes people are legally owed notice of. Feature news sent this way
          ignores their opt-out.{locked && ' The kind is fixed once the draft is saved.'}
        </p>
      )}
    </div>
  )
}

export function ServiceNoticeMark() {
  return (
    <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={OPTIONS[1].icon} />
      </svg>
      Service notice · reaches every account
    </span>
  )
}
